/* ===== Admin Collapsible Sections ===== */
import { $, fmtDate } from '../utils/helpers.js?v=83.79';
import { getCurrentSettings, syncClientPromoFieldsVisibility } from '../services/settings.js?v=83.79';
import { getLastClaimsRows } from '../services/claims.js?v=83.79';
import { getCurrentUser } from '../services/auth.js?v=83.79';
import { logger } from '../utils/logger.js?v=83.79';
import { showSnackbar } from '../utils/ui.js?v=83.79';
import {
  getClientInterfaceSubsectionScope,
  closeClientInterfaceDrilldown
} from './client-interface-nav.js?v=83.79';
import { updateModClientCardInfo } from './mod-client-modules.js?v=83.79';
import { isLoyaltyVisible } from './loyalty-module.js?v=83.79';

const STORAGE_KEY = 'sj_admin_sections';

// Guard against double-toggle (touch + click on mobile)
const toggleGuards = new Map();

// Track touch events to distinguish tap from scroll
const touchTrackers = new Map();

// Get saved state from localStorage
function getSavedState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
}

// Save state to localStorage
function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    logger.warn('Failed to save admin sections state:', e);
  }
}

// Toggle single section
export function toggleAdminSection(sectionId, event) {
  // Guard: ignore clicks on info button
  if (event && event.target) {
    if (event.target.closest('.admin-section-info-btn')) {
      return; // Don't toggle if click is on info button
    }
  }

  // Guard against double-toggle (touch + click)
  const now = Date.now();
  const lastToggle = toggleGuards.get(sectionId) || 0;
  if (now - lastToggle < 300) {
    return; // Ignore if called within 300ms
  }
  toggleGuards.set(sectionId, now);

  const section = document.querySelector(`.admin-section[data-section="${sectionId}"]`);
  if (!section) return;

  const isExpanded = section.classList.contains('expanded');
  
  if (isExpanded) {
    section.classList.remove('expanded');
    // If collapsing Client Interface Configuration, also collapse all subsections
    if (sectionId === 'client-interface') {
      closeClientInterfaceDrilldown();
      const subsections = section.querySelectorAll('.admin-subsection');
      subsections.forEach(subsection => {
        subsection.classList.remove('expanded');
      });
    }
  } else {
    section.classList.add('expanded');
    if (sectionId === 'client-interface') {
      closeClientInterfaceDrilldown();
    }
    if (sectionId === 'client-mode') {
      import('./mod-client-modules.js?v=83.79')
        .then((m) => m.refreshModClientModulesAsync())
        .catch(() => {});
    }
    
    // ✅ Scroll to section header when expanding - ensures entire section is visible at top
    // Wait for DOM to update and expansion animation to start
    setTimeout(() => {
      const sectionHeader = section.querySelector('.admin-section-header');
      
      if (sectionHeader) {
        // Use scrollIntoView with scroll-margin-top CSS for better compatibility
        // CSS scroll-margin-top handles the offset automatically (80px set in CSS)
        sectionHeader.scrollIntoView({
          behavior: 'smooth',
          block: 'start', // Align to top of viewport (respects scroll-margin-top)
          inline: 'nearest'
        });
      } else {
        // Fallback: scroll to section itself
        section.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 250); // Delay to allow expansion animation to complete and DOM to settle
  }

  // Save state
  const state = getSavedState();
  state[sectionId] = !isExpanded;
  saveState(state);

  // Update summaries after expand
  if (!isExpanded) {
    setTimeout(() => updateSectionSummaries(), 100);
  }
}

// Toggle all sections
export function toggleAllAdminSections() {
  const sections = document.querySelectorAll('.admin-section');
  const allExpanded = Array.from(sections).every(s => s.classList.contains('expanded'));
  
  const state = {};
  sections.forEach(section => {
    const sectionId = section.getAttribute('data-section');
    if (!sectionId) return;

    if (allExpanded) {
      section.classList.remove('expanded');
      state[sectionId] = false;
    } else {
      section.classList.add('expanded');
      state[sectionId] = true;
    }
  });

  saveState(state);
  
  // Update button text
  const btn = document.querySelector('.btn-expand-all');
  const textSpan = document.getElementById('expandAllText');
  if (textSpan) {
    textSpan.textContent = allExpanded ? 'Extinde tot' : 'Restrânge tot';
  }

  // Update summaries after expand
  if (!allExpanded) {
    setTimeout(() => updateSectionSummaries(), 200);
  }
}

// Helper function to create status badge HTML
function createStatusBadge(text, isActive) {
  const statusClass = isActive ? 'status-badge status-badge-active' : 'status-badge status-badge-inactive';
  const statusText = isActive ? text : text.replace('Activ', 'Inactiv').replace('Permis', 'Blocat').replace('ON', 'OFF').replace('Configurat', 'Neconfigurat').replace('Protejat', 'Deschis');
  return `<span class="${statusClass}">${statusText}</span>`;
}

// Helper function to format summary with status badges
function formatSummaryWithBadges(parts) {
  return parts.join(' • ');
}

// Update section summaries
export function updateSectionSummaries() {
  const currentSettings = getCurrentSettings();
  if (!currentSettings) return;

  // Client Interface Configuration summary
  const clientInterfaceSummary = document.getElementById('clientInterfaceSummary');
  if (clientInterfaceSummary) {
    const hasAdminPass = true;
    const bgCount = (currentSettings.bgURLs || []).length;
    const prizes = (currentSettings.prizes || []).filter(p => (parseInt(p.count) || 0) > 0);
    const parts = [];
    parts.push(createStatusBadge('Interfață: Activă', hasAdminPass));
    parts.push(`Fundaluri: ${bgCount}/5`);
    parts.push(`Recompense: ${prizes.length}`);
    clientInterfaceSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // Quick Actions summary - removed (no longer needed, subtitle shows this info)

  // Claims Dashboard summary
  const claimsSummary = document.getElementById('claimsSummary');
  if (claimsSummary) {
    const claimsRows = getLastClaimsRows();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // ✅ FIX: Convert Firestore Timestamp to Date correctly
    const todayClaims = claimsRows.filter(claim => {
      if (!claim.date) return false;
      
      let claimDate;
      try {
        // Check if it's a Firestore Timestamp (has toDate method)
        if (claim.date && typeof claim.date.toDate === 'function') {
          claimDate = claim.date.toDate();
        } else if (claim.date && typeof claim.date.toMillis === 'function') {
          // Alternative: if it has toMillis, convert via timestamp
          claimDate = new Date(claim.date.toMillis());
        } else if (claim.date.seconds) {
          // Firestore Timestamp format: { seconds, nanoseconds }
          claimDate = new Date(claim.date.seconds * 1000);
        } else {
          // Try standard Date conversion
          claimDate = new Date(claim.date);
        }
        
        // Validate the date
        if (!claimDate || isNaN(claimDate.getTime())) {
          return false;
        }
        
        // Compare dates (reset hours to compare only the day)
        claimDate.setHours(0, 0, 0, 0);
        return claimDate.getTime() === today.getTime();
      } catch (e) {
        logger.error('[updateSectionSummaries] Error converting claim date:', e, claim.date);
        return false;
      }
    });
    
    const parts = [];
    parts.push(`Astăzi: ${todayClaims.length} revendicări`);
    parts.push(`Total revendicări: ${claimsRows.length}`);
    parts.push(createStatusBadge('Rapoarte disponibile', true));
    
    claimsSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // Data & Compliance summary
  // Data & Compliance section removed - moved to Account page
  const dataComplianceSummary = null;
  if (false && dataComplianceSummary) {
    const company = currentSettings.companyName || '';
    const cui = currentSettings.cui || '';
    const email = currentSettings.operatorEmail || '';
    const isComplete = company && cui && email;
    const hasAdminPass = true;
    
    const parts = [];
    parts.push(createStatusBadge('GDPR: Activ', isComplete));
    parts.push(createStatusBadge('Securitate: Activă', hasAdminPass));
    
    dataComplianceSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // Nested subsection summaries (for cards within sections)
  
  // Operator summary (nested in Data & Compliance)
  // Operator summary removed - moved to Account page
  const opSummary = null;
  if (opSummary) {
    const company = currentSettings.companyName || '';
    const cui = currentSettings.cui || '';
    const email = currentSettings.operatorEmail || '';
    const isComplete = company && cui && email;
    
    const parts = [];
    if (company) parts.push(`Firmă: ${company}`);
    parts.push(createStatusBadge('GDPR: Activ', isComplete));
    parts.push(createStatusBadge('Marketing: Permis', true));
    
    opSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // Admin summary (nested in Client Interface)
  const adminSummary = document.getElementById('adminSummary');
  if (adminSummary) {
    const hasAdminPass = true;
    const parts = [];
    parts.push(createStatusBadge('Interfață: Activă', true));
    parts.push(createStatusBadge('Acces: Administrator', hasAdminPass));
    
    adminSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // Images summary (nested in Client Interface)
  const imagesSummary = document.getElementById('imagesSummary');
  if (imagesSummary) {
    const bgURLs = currentSettings.bgURLs || [];
    const bgCount = bgURLs.length;
    const maxBg = 5;
    const confSet = !!(currentSettings.confirmBgURL);
    
    const parts = [];
    parts.push(`Fundaluri: ${bgCount}/${maxBg}`);
    parts.push(createStatusBadge('Confirmare: ON', confSet));
    
    imagesSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // Prizes summary (nested in Client Interface)
  const prizesSummary = document.getElementById('prizesSummary');
  if (prizesSummary) {
    const prizes = (currentSettings.prizes || []).filter(p => (parseInt(p.count) || 0) > 0);
    const totalCount = prizes.reduce((sum, p) => sum + (parseInt(p.count) || 0), 0);
    const hasActivePrizes = prizes.length > 0 && totalCount > 0;
    
    const parts = [];
    parts.push(`Tipuri: ${prizes.length}`);
    parts.push(`Stoc: ${totalCount}`);
    parts.push(createStatusBadge('Distribuție: Activă', hasActivePrizes));
    
    prizesSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // No-win summary (nested in Client Interface)
  const noWinSummary = document.getElementById('noWinSummary');
  if (noWinSummary) {
    const msg = currentSettings.defaultPrizeMsg || '';
    const hasImg = !!(currentSettings.defaultPrizeImg);
    const msgSet = msg.trim().length > 0;
    
    const parts = [];
    parts.push(createStatusBadge('Text: Configurat', msgSet));
    parts.push(createStatusBadge('Vizual: Activ', hasImg));
    
    noWinSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // Account summary (nested in Data & Compliance)
  // Account summary removed - moved to Account page
  const accountSummary = null;
  if (false && accountSummary) {
    const hasAdminPass = true;
    
    const parts = [];
    parts.push(createStatusBadge('Securitate: Activă', hasAdminPass));
    parts.push(createStatusBadge('Acces: Protejat', hasAdminPass));
    
    accountSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  // Mod Client card info
  updateModClientCardInfo(currentSettings);
  
  // Load archived campaigns if on dashboard
  if (document.getElementById('archivedCampaignsList')) {
    setTimeout(() => {
      if (typeof window.loadArchivedCampaigns === 'function') {
        // Dashboard now uses compact card + modal, so we don't load campaigns inline
    // Only update badge count
    if (window.updateClosedCampaignsBadge) {
      window.updateClosedCampaignsBadge();
    }
      }
    }, 200);
  }
  
  // Feedback summary
  const feedbackSummary = document.getElementById('feedbackSummary');
  if (feedbackSummary) {
    const hasTap = !!(currentSettings?.clientFeedbackUrl || '').trim();
    if (currentSettings?.feedbackEnabled && hasTap) {
      feedbackSummary.textContent = 'Activat';
    } else if (currentSettings?.feedbackEnabled) {
      feedbackSummary.textContent = 'Activat (fără link)';
    } else {
      feedbackSummary.textContent = 'Dezactivat';
    }
  }

  // Meniu summary
  const menuLocationSummary = document.getElementById('menuLocationSummary');
  if (menuLocationSummary) {
    const label = String(currentSettings?.menuLabel || 'Meniu').trim() || 'Meniu';
    const url = (currentSettings?.menuUrl || '').trim();
    const imgCount = Array.isArray(currentSettings?.menuImages)
      ? currentSettings.menuImages.filter((u) => String(u || '').trim()).length
      : 0;
    const on = currentSettings?.menuEnabled !== false;
    const parts = [];
    parts.push(label);
    if (!on) {
      parts.push('Dezactivat');
    } else if (url && imgCount > 0) {
      parts.push('Link + galerie');
    } else if (url) {
      parts.push('Link activ');
    } else if (imgCount > 0) {
      parts.push(`${imgCount} imagini`);
    } else {
      parts.push('Neconfigurat');
    }
    menuLocationSummary.textContent = parts.join(' · ');
  }

  // Social media summary
  const socialMediaSummary = document.getElementById('socialMediaSummary');
  if (socialMediaSummary) {
    const sm = currentSettings?.socialMedia || {};
    const parts = [];
    for (const p of ['Facebook', 'Instagram', 'TikTok']) {
      const d = sm[p.toLowerCase()] || {};
      parts.push(createStatusBadge(p, d.enabled === true && String(d.link || '').trim().length > 0));
    }
    socialMediaSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  const wifiSummary = document.getElementById('wifiSummary');
  if (wifiSummary) {
    const wOn = currentSettings?.wifiEnabled === true;
    const hasSsid = String(currentSettings?.wifiSsid || '').trim().length > 0;
    const parts = [];
    parts.push(wOn ? 'Activ pe client' : 'Dezactivat');
    parts.push(createStatusBadge('Rețea configurată', hasSsid));
    wifiSummary.innerHTML = formatSummaryWithBadges(parts);
  }

  const communitySummary = document.getElementById('communitySummary');
  if (communitySummary) {
    const enabled = currentSettings?.communityEnabled === true;
    const hasCustomMessage = String(currentSettings?.communityMessage || '').trim().length > 0;
    const parts = [];
    parts.push(enabled ? 'Activ pe client' : 'Dezactivat');
    parts.push(createStatusBadge('Mesaj custom', hasCustomMessage));
    communitySummary.innerHTML = formatSummaryWithBadges(parts);
  }

  const locationLogoSummary = document.getElementById('locationLogoSummary');
  if (locationLogoSummary) {
    const hasLogo = String(currentSettings?.locationLogoURL || '').trim().length > 0;
    locationLogoSummary.textContent = hasLogo ? 'Logo setat' : 'Fără logo';
  }

  const locationMapSummary = document.getElementById('locationMapSummary');
  if (locationMapSummary) {
    const on = currentSettings?.locationMapEnabled === true;
    const hasUrl = String(currentSettings?.locationMapUrl || '').trim().length > 0;
    const name = String(currentSettings?.locationMapName || '').trim();
    if (!on) {
      locationMapSummary.textContent = 'Locație / Hartă inactivă';
    } else if (hasUrl) {
      locationMapSummary.textContent = name ? `Activ · ${name}` : 'Activ · link setat';
    } else {
      locationMapSummary.textContent = 'Activ · fără link Maps';
    }
  }

  const reservationSummary = document.getElementById('reservationSummary');
  if (reservationSummary) {
    const label = String(currentSettings?.reservationLabel || 'Rezervare').trim() || 'Rezervare';
    const on = currentSettings?.reservationEnabled === true;
    const hasContact = ['reservationPhone', 'reservationWhatsapp', 'reservationLink']
      .some((k) => String(currentSettings?.[k] || '').trim());
    const parts = [label];
    if (!on) {
      parts.push('Dezactivat');
    } else if (hasContact) {
      parts.push('Activ');
    } else {
      parts.push('Fără contact');
    }
    reservationSummary.textContent = parts.join(' · ');
  }

  const locationHoursSummary = document.getElementById('locationHoursSummary');
  if (locationHoursSummary) {
    const on = currentSettings?.locationHoursEnabled === true;
    const hasImg = String(currentSettings?.locationHoursImageURL || '').trim().length > 0;
    if (!on) {
      locationHoursSummary.textContent = 'Program locație inactiv';
    } else if (hasImg) {
      locationHoursSummary.textContent = 'Program locație activ · imagine setată';
    } else {
      locationHoursSummary.textContent = 'Program locație activ · fără imagine';
    }
  }

  const clientPromoGallerySummary = document.getElementById('clientPromoGallerySummary');
  if (clientPromoGallerySummary) {
    const g = currentSettings?.clientPromoGallery;
    const on = g?.enabled === true;
    const n = Array.isArray(g?.images) ? g.images.filter((u) => String(u || '').trim()).length : 0;
    if (!on) {
      clientPromoGallerySummary.textContent = 'Dezactivat';
    } else {
      clientPromoGallerySummary.textContent = `Activ · ${n} imagini`;
    }
  }

  const eventModuleSummary = document.getElementById('eventModuleSummary');
  const eventSummaryText = (() => {
    const em = currentSettings?.eventModule;
    const on = em?.enabled === true;
    const n = Array.isArray(em?.images) ? em.images.filter((u) => String(u || '').trim()).length : 0;
    if (!on) return 'Dezactivat';
    return `Activ · ${n} poze`;
  })();
  if (eventModuleSummary) {
    eventModuleSummary.textContent = eventSummaryText;
  }
  const eventModuleCardSummary = document.getElementById('eventModuleCardSummary');
  if (eventModuleCardSummary) {
    eventModuleCardSummary.textContent = eventSummaryText;
  }

  const campanieModuleCardSummary = document.getElementById('campanieModuleCardSummary');
  if (campanieModuleCardSummary) {
    const status = currentSettings?.campaignStatus || 'draft';
    const isActive = status === 'active';
    const tries = currentSettings?.totalTries ?? '—';
    const prizeCount = Array.isArray(currentSettings?.prizes) ? currentSettings.prizes.length : 0;
    campanieModuleCardSummary.textContent = isActive
      ? `${tries} încercări · ${prizeCount} recompense`
      : 'Dezactivat';
  }

  const loyaltySummaryText = (() => {
    const ls = currentSettings?.loyaltySettings;
    const on = ls?.active === true;
    const req = ls?.requiredPurchases ?? 7;
    if (!on) return 'Dezactivat';
    const campaignOn = (currentSettings?.campaignStatus || 'draft') === 'active';
    const start = ls?.startDate ? fmtDate(ls.startDate) : '';
    const end = ls?.endDate ? fmtDate(ls.endDate) : '';
    const period = start || end ? ` · ${start || '…'} – ${end || '…'}` : '';
    if (campaignOn) return `Activ · ascuns (campania rulează)${period}`;
    if (!isLoyaltyVisible(currentSettings) && ls?.startDate) {
      const today = new Date().toISOString().slice(0, 10);
      if (String(ls.startDate).trim() > today) return `Activ · începe ${start}`;
    }
    return `Activ · ${req} achiziții / recompensă${period}`;
  })();
  const loyaltyModuleSummary = document.getElementById('loyaltyModuleSummary');
  if (loyaltyModuleSummary) loyaltyModuleSummary.textContent = loyaltySummaryText;
  const fidelizareModuleCardSummary = document.getElementById('fidelizareModuleCardSummary');
  if (fidelizareModuleCardSummary) fidelizareModuleCardSummary.textContent = loyaltySummaryText;
}

// Initialize admin sections on page load
export function initAdminSections() {
  const savedState = getSavedState();
  
  document.querySelectorAll('.admin-section').forEach(section => {
    const sectionId = section.getAttribute('data-section');
    if (!sectionId) return;

    // Apply saved state
    if (savedState[sectionId]) {
      section.classList.add('expanded');
    }

    // Add touch event support for main section headers (mobile)
    // Detect tap vs scroll to prevent accidental expand on scroll
    const sectionHeader = section.querySelector('.admin-section-header');
    if (sectionHeader) {
      const trackerId = `section-${sectionId}`;
      
      sectionHeader.addEventListener('touchstart', (e) => {
        const touch = e.touches[0];
        touchTrackers.set(trackerId, {
          startX: touch.clientX,
          startY: touch.clientY,
          isScrolling: false
        });
      }, { passive: true });
      
      sectionHeader.addEventListener('touchmove', (e) => {
        const tracker = touchTrackers.get(trackerId);
        if (!tracker) return;
        
        const touch = e.touches[0];
        const dy = Math.abs(touch.clientY - tracker.startY);
        const dx = Math.abs(touch.clientX - tracker.startX);
        
        // If vertical or horizontal movement > 10px, it's a scroll
        if (dy > 10 || dx > 10) {
          tracker.isScrolling = true;
        }
      }, { passive: true });
      
      sectionHeader.addEventListener('touchend', (e) => {
        const tracker = touchTrackers.get(trackerId);
        if (!tracker) return;

        // Dacă tap-ul este pe butonul de info, nu blocăm evenimentul –
        // lăsăm click-ul să fie procesat de handlerul data-action (deschide popup-ul).
        if (e.target && e.target.closest('.admin-section-info-btn')) {
          touchTrackers.delete(trackerId);
          return;
        }

        // Only toggle if it wasn't a scroll
        if (!tracker.isScrolling) {
          e.preventDefault();
          e.stopPropagation();
          toggleAdminSection(sectionId, e);
        }
        
        // Clean up tracker
        touchTrackers.delete(trackerId);
      }, { passive: false });
    }

    // Ensure all subsections in Client Interface Configuration start collapsed
    if (sectionId === 'client-interface') {
      const subsections = section.querySelectorAll('.admin-subsection');
      subsections.forEach(subsection => {
        subsection.classList.remove('expanded');

        if (subsection.hasAttribute('data-no-expand')) return;

        // Add touch event support for mobile
        const header = subsection.querySelector('.admin-subsection-header');
        if (header) {
          const subsectionId = subsection.getAttribute('data-subsection');
          if (subsectionId) {
            const trackerId = `subsection-${subsectionId}`;
            
            // Track touch start position
            header.addEventListener('touchstart', (e) => {
              const touch = e.touches[0];
              touchTrackers.set(trackerId, {
                startX: touch.clientX,
                startY: touch.clientY,
                isScrolling: false
              });
              e.stopPropagation();
            }, { passive: true });
            
            // Detect scroll during touch move
            header.addEventListener('touchmove', (e) => {
              const tracker = touchTrackers.get(trackerId);
              if (!tracker) return;
              
              const touch = e.touches[0];
              const dy = Math.abs(touch.clientY - tracker.startY);
              const dx = Math.abs(touch.clientX - tracker.startX);
              
              // If vertical or horizontal movement > 10px, it's a scroll
              if (dy > 10 || dx > 10) {
                tracker.isScrolling = true;
              }
            }, { passive: true });
            
            // Only toggle if it wasn't a scroll
            header.addEventListener('touchend', (e) => {
              const tracker = touchTrackers.get(trackerId);
              if (!tracker) return;
              
              // Only toggle if it wasn't a scroll
              if (!tracker.isScrolling) {
                e.preventDefault();
                e.stopPropagation();
                toggleClientInterfaceSubsection(subsectionId);
              }
              
              // Clean up tracker
              touchTrackers.delete(trackerId);
            }, { passive: false });
          }
        }
      });
    }

    // Update button text
    const allSections = document.querySelectorAll('.admin-section');
    const allExpanded = Array.from(allSections).every(s => s.classList.contains('expanded'));
    const textSpan = document.getElementById('expandAllText');
    if (textSpan) {
      textSpan.textContent = allExpanded ? 'Restrânge tot' : 'Extinde tot';
    }
  });

  // Update summaries
  setTimeout(() => updateSectionSummaries(), 200);

  const modClientSection = document.querySelector('.admin-section[data-section="client-mode"]');
  if (modClientSection?.classList.contains('expanded')) {
    import('./mod-client-modules.js?v=83.79')
      .then((m) => m.refreshModClientModulesAsync())
      .catch(() => {});
  }
}


// Toggle nested subsection in Client Interface Configuration (accordion behavior)
export function toggleClientInterfaceSubsection(subsectionId) {
  // Guard against double-toggle (touch + click)
  const now = Date.now();
  const guardKey = `subsection-${subsectionId}`;
  const lastToggle = toggleGuards.get(guardKey) || 0;
  if (now - lastToggle < 300) {
    return; // Ignore if called within 300ms
  }
  toggleGuards.set(guardKey, now);
  
  const parentSection = document.querySelector('.admin-section[data-section="client-interface"]');
  if (!parentSection) return;

  const scope = getClientInterfaceSubsectionScope() || parentSection;

  const allSubsections = scope.querySelectorAll('.admin-subsection');
  const targetSubsection = parentSection.querySelector(`.admin-subsection[data-subsection="${subsectionId}"]`);
  
  if (!targetSubsection) return;
  if (targetSubsection.hasAttribute('data-no-expand')) return;

  const isExpanded = targetSubsection.classList.contains('expanded');

  // Accordion behavior: close all other subsections
  allSubsections.forEach(subsection => {
    subsection.classList.remove('expanded');
  });

  // Toggle the clicked subsection (if it was closed, open it; if it was open, keep it closed)
  if (!isExpanded) {
    targetSubsection.classList.add('expanded');

    if (subsectionId === 'client-promo-gallery') {
      try {
        syncClientPromoFieldsVisibility();
      } catch (e) {
        logger.warn('[admin-collapsible] syncClientPromoFieldsVisibility failed:', e);
      }
    }

    // ✅ Scroll to subsection header when expanding - ensures entire subsection is visible at top
    // Wait longer for accordion animations to complete (closing other subsections)
    setTimeout(() => {
      const subsectionHeader = targetSubsection.querySelector('.admin-subsection-header');
      
      if (!subsectionHeader) {
        // Fallback: scroll to subsection itself
        targetSubsection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
        return;
      }
      
      // Check if header is already visible at the top (within 100px of target position)
      const rect = subsectionHeader.getBoundingClientRect();
      const dashboardHeader = document.querySelector('.dashboard-header');
      const headerHeight = dashboardHeader ? dashboardHeader.offsetHeight : 0;
      const targetTop = headerHeight + 20; // 20px padding
      
      // Only scroll if header is not already at the correct position
      if (Math.abs(rect.top - targetTop) > 50) {
        // Use scrollIntoView with scroll-margin-top CSS for better compatibility
        subsectionHeader.scrollIntoView({
          behavior: 'smooth',
          block: 'start', // Align to top of viewport (respects scroll-margin-top)
          inline: 'nearest'
        });
      }
    }, 400); // Increased delay to allow accordion animations to complete
  }
}

// Make functions available globally
window.toggleAdminSection = toggleAdminSection;
window.toggleAllAdminSections = toggleAllAdminSections;
window.updateSectionSummaries = updateSectionSummaries;
window.toggleClientInterfaceSubsection = toggleClientInterfaceSubsection;